import React, { useState, useEffect } from 'react';
import { FiCalendar, FiColumns } from 'react-icons/fi';
import { useProject } from '../../context/ProjectContext';
import visitService from '../../services/visitService';
import api from '../../services/api';
import { VISIT_TYPES, formatVisitId } from '../../utils/constants';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { visits as visitMessages } from '../../utils/validationMessages';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import VisitCalendar from './VisitCalendar';
import PlannerVisitActionsModal from './PlannerVisitActionsModal';
import ScheduleVisitModal from './ScheduleVisitModal';
import './VisitManagement.css';

const VisitSchedulingPlanner = () => {
  const { currentProject } = useProject();
  const projectCode = currentProject && currentProject !== 'all' ? currentProject : null;

  const [plannerData, setPlannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePermissions, setProfilePermissions] = useState({});
  const [siteFilter, setSiteFilter] = useState('all');
  const [sites, setSites] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [selectedSuggested, setSelectedSuggested] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalMode, setScheduleModalMode] = useState('create');
  const [scheduleModalInitialVisit, setScheduleModalInitialVisit] = useState(null);
  const [scheduleModalSuggestedDate, setScheduleModalSuggestedDate] = useState(null);
  const [confirmScheduleAfterSave, setConfirmScheduleAfterSave] = useState(false); // true when user chose "Confirm Schedule" on suggested SMV
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'panel'
  const [toast, setToast] = useState(null);

  const isViewOnly = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const canSchedule = !isViewOnly && (profilePermissions.iScheduleModify === 1 || profilePermissions.iScheduleModify === '1');

  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) setProfilePermissions(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectCode]);

  const fetchPlanner = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await visitService.getPlanner(projectCode || undefined);
      setPlannerData(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load planner data');
      setPlannerData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanner();
  }, [projectCode]);

  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      setLoadingFilters(true);
      try {
        const url = projectCode ? `/sites?project=${projectCode}` : '/sites';
        const response = await api.get(url);
        const list = (response.data || [])
          .map(site => {
            const siteCode = (site.siteCode ?? site.vSiteCode ?? '').toString().trim();
            const siteDescription = (site.sitedescription ?? site.siteDescription ?? site.SiteDescription ?? '').toString().trim();
            const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
            return {
              siteID: site.siteID,
              siteCode,
              siteName: site.sitedescription || site.siteCode || '',
              siteDisplay,
              projectCode: site.projectCode || null,
            };
          })
          .filter(site => site.siteCode || site.siteName)
          .sort((a, b) => (a.siteDisplay || a.siteCode).localeCompare(b.siteDisplay || b.siteCode));
        if (!cancelled) setSites(list);
      } catch (err) {
        if (!cancelled) setSites([]);
      } finally {
        if (!cancelled) setLoadingFilters(false);
      }
    };
    loadSites();
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleEventClick = async (visitOrSuggested) => {
    if (visitOrSuggested?.isSuggested) {
      if (!canSchedule) return;
      setSelectedSuggested(visitOrSuggested);
      setActionsModalOpen(true);
    } else if (visitOrSuggested?.scheduledVisitID) {
      if (!canSchedule) return;
      const visitId = visitOrSuggested.scheduledVisitID ?? visitOrSuggested.scheduledVisitId;
      let visitToOpen = visitOrSuggested;
      try {
        // Fetch latest visit so we have correct VisitStatus (Confirmed => view-only modal)
        const latest = await visitService.getById(visitId);
        if (latest) visitToOpen = latest;
      } catch (_) {
        /* use calendar visit if fetch fails */
      }
      setScheduleModalInitialVisit(visitToOpen);
      setScheduleModalMode('edit');
      setScheduleModalSuggestedDate(null);
      setScheduleModalOpen(true);
    }
  };

  const buildInitialVisitFromSuggested = () => {
    if (!selectedSuggested) return null;
    const dateStr = selectedSuggested.suggestedDate?.split?.('T')[0] || selectedSuggested.suggestedDate;
    return {
      siteID: selectedSuggested.siteId,
      studyID: selectedSuggested.studyId,
      visitType: 'SMV',
      plannedStartDate: dateStr,
      plannedEndDate: dateStr,
      cra_UserID: null,
    };
  };

  const handleConfirmSchedule = () => {
    if (!selectedSuggested || !canSchedule) return;
    // Suggested SMVs are not in DB yet. Confirm = save as actual visit in DB + set Confirmed; later opening it shows details only (no modify rights).
    setConfirmScheduleAfterSave(true);
    setScheduleModalSuggestedDate(selectedSuggested.suggestedDate);
    setScheduleModalInitialVisit(buildInitialVisitFromSuggested());
    setScheduleModalMode('create');
    setActionsModalOpen(false);
    setScheduleModalOpen(true);
  };

  const handleModifySchedule = () => {
    if (!selectedSuggested || !canSchedule) return;
    setConfirmScheduleAfterSave(false); // User will edit and save; visit stays Draft/Planned until they confirm elsewhere
    setScheduleModalSuggestedDate(selectedSuggested.suggestedDate);
    setScheduleModalInitialVisit(buildInitialVisitFromSuggested());
    setScheduleModalMode('create');
    setActionsModalOpen(false);
    setScheduleModalOpen(true);
  };

  const handleScheduleModalClose = () => {
    setScheduleModalOpen(false);
    setScheduleModalInitialVisit(null);
    setScheduleModalSuggestedDate(null);
    setConfirmScheduleAfterSave(false);
  };

  const handleScheduleSaved = () => {
    setToast({ type: 'success', message: visitMessages.savedSuccess });
    handleScheduleModalClose();
    fetchPlanner();
  };

  const matchSite = (siteName, siteCode) => {
    if (siteFilter === 'all') return true;
    const name = (siteName || '').toString().trim();
    const code = (siteCode || '').toString().trim();
    return siteFilter === name || siteFilter === code;
  };
  const savedSmvVisits = (plannerData?.savedSmvVisits || []).filter((v) =>
    matchSite(v.siteName || v.SiteName || v.siteCode, v.siteCode || v.SiteCode || v.siteName || v.SiteName)
  );
  const suggestedRows = (plannerData?.sitesWithSuggestions || [])
    .filter((site) => matchSite(site.siteName || site.SiteName, site.siteCode || site.SiteCode))
    .flatMap((site) =>
      (site.suggestedDates || []).map((dateStr) => {
        const startStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
        return startStr
          ? {
              siteId: site.siteId,
              siteName: site.siteName || site.siteCode,
              siteCode: site.siteCode,
              projectCode: site.projectCode,
              studyId: site.studyId,
              suggestedDate: startStr,
              suggestedPayload: {
                isSuggested: true,
                suggestedDate: startStr,
                siteId: site.siteId,
                siteName: site.siteName,
                siteCode: site.siteCode,
                projectCode: site.projectCode,
                studyId: site.studyId,
                piName: site.pIName,
                frequency: site.frequency,
                nSiteVisits: site.nSiteVisits,
              },
            }
          : null;
      })
    )
    .filter(Boolean);

  return (
    <div className="visit-management">
      {/* Filter by Site - same as Visit Schedule Module */}
      <div className="visit-filters">
        <div className="filter-group">
          <label className="form-label">Filter by Site</label>
          <select
            className="form-select"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            disabled={loadingFilters}
          >
            <option value="all">All Sites</option>
            {loadingFilters ? (
              <option value="">Loading sites...</option>
            ) : (
              sites.map(site => (
                <option key={site.siteID} value={site.siteCode}>{site.siteDisplay || site.siteName}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {error && (
        <Alert type="danger" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* View Toggle: Calendar | Panel */}
      {!loading && (
        <div className="view-toggle">
          <button
            type="button"
            className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('calendar')}
          >
            <FiCalendar /> Calendar View
          </button>
          <button
            type="button"
            className={`btn ${viewMode === 'panel' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('panel')}
          >
            <FiColumns /> Panel View
          </button>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : viewMode === 'panel' ? (
        <div className="visit-content-area">
          <div className="visit-panel-view">
            <div className="visit-table-container">
              <h4 style={{ marginBottom: '12px' }}>Saved SMV Visits</h4>
              <table className="visit-table">
                <thead>
                  <tr>
                    <th>Visit ID</th>
                    <th>Visit Type</th>
                    <th>Site</th>
                    <th>Scheduled Start Date</th>
                    <th>Scheduled End Date</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedSmvVisits.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                        No saved SMV visits
                      </td>
                    </tr>
                  ) : (
                    savedSmvVisits.map((v) => (
                      <tr key={`smv-${v.scheduledVisitID ?? v.scheduledVisitId}`}>
                        <td>{formatVisitId(v)}</td>
                        <td>{VISIT_TYPES[v.visitType] || v.visitType || 'SMV'}</td>
                        <td>{v.siteCode || v.siteName || v.SiteCode || v.SiteName || '—'}</td>
                        <td>{v.plannedStartDate ? formatDateDDMMMYYYY(v.plannedStartDate) : '—'}</td>
                        <td>{v.plannedEndDate ? formatDateDDMMMYYYY(v.plannedEndDate) : '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          {canSchedule && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleEventClick(v)}
                            >
                              View Details
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="visit-table-container" style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '12px' }}>Suggested SMV Dates</h4>
              <table className="visit-table">
                <thead>
                  <tr>
                    <th>Site Name</th>
                    <th>Suggested Date</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestedRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                        No suggested SMV dates
                      </td>
                    </tr>
                  ) : (
                    suggestedRows.map((row, idx) => (
                      <tr key={`suggested-${row.siteId}-${row.suggestedDate}-${idx}`}>
                        <td>{row.siteName || row.siteCode || '—'}</td>
                        <td>{row.suggestedDate ? formatDateDDMMMYYYY(row.suggestedDate + 'T12:00:00') : '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          {canSchedule && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedSuggested(row.suggestedPayload);
                                setActionsModalOpen(true);
                              }}
                            >
                              Modify Schedule Visit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <VisitCalendar
          visits={[]}
          activeStage="schedule"
          filters={{ site: siteFilter, cra: 'all' }}
          plannerData={plannerData}
          onEventClick={handleEventClick}
          onDateClick={() => {}}
        />
      )}

      <PlannerVisitActionsModal
        isOpen={actionsModalOpen}
        onClose={() => setActionsModalOpen(false)}
        suggested={selectedSuggested}
        onConfirmSchedule={handleConfirmSchedule}
        onModifySchedule={handleModifySchedule}
        canSchedule={canSchedule}
      />

      <ScheduleVisitModal
        isOpen={scheduleModalOpen}
        onClose={handleScheduleModalClose}
        selectedDate={scheduleModalSuggestedDate ? new Date(scheduleModalSuggestedDate + 'T12:00:00') : null}
        onSave={handleScheduleSaved}
        mode={scheduleModalMode}
        existingVisit={scheduleModalInitialVisit}
        plannerMode={true}
        confirmAfterSave={confirmScheduleAfterSave}
      />
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
};

export default VisitSchedulingPlanner;
